import { GROK_WORKER_URL } from '../../../js/config.js';
import { tokenManager } from '../../../js/utils/TokenManager.js';
import { parseWorkerErrorBody, buildWorkerErrorDetails } from '../shared/worker-error.js';
import { handleAuthenticationRequired } from './hangout-auth-modals.js';

export const hangoutWorkerUrl = (path) =>
    `${GROK_WORKER_URL.replace(/\/+$/, '')}${path.startsWith('/') ? path : `/${path}`}`;

const jsonPostInit = (requestBody) => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody)
});

export async function makeWorkerRequest(workerUrl, requestBody, options = {}) {
    const { allowUnauthenticated = false } = options;

    let response;
    try {
        if (tokenManager.isAuthenticated()) {
            response = await tokenManager.authenticatedFetch(workerUrl, jsonPostInit(requestBody));
        } else if (allowUnauthenticated) {
            response = await fetch(workerUrl, jsonPostInit(requestBody));
        } else {
            throw new Error('Authentication required');
        }
    } catch (error) {
        console.error('hangout-worker-request: Authentication error:', error);

        if (error.message.includes('401') || error.message.includes('auth') || error.message.includes('token')) {
            if (allowUnauthenticated) {
                response = await fetch(workerUrl, jsonPostInit(requestBody));
            } else {
                await handleAuthenticationRequired();
                throw new Error('Authentication required');
            }
        } else {
            throw error;
        }
    }

    if (!response.ok) {
        const rawErrorText = await response.text().catch(() => '');
        const parsedBody = parseWorkerErrorBody(rawErrorText);
        const workerError = buildWorkerErrorDetails(response, parsedBody);

        if ((response.status === 429 || response.status === 403) && parsedBody.data) {
            try {
                if (parsedBody.data.code === 'model_lane_forbidden') {
                    const { showModelAccessRequiredModal } = await import('../../utils/quotaErrorHandler.js');
                    await showModelAccessRequiredModal({
                        lane: parsedBody.data.lane === 'max' ? 'max' : 'plus'
                    });
                    const laneHandledError = new Error('QUOTA_HANDLED');
                    Object.assign(laneHandledError, { isQuotaHandled: true, workerError });
                    throw laneHandledError;
                }
                const { handleQuotaError } = await import('../../utils/quotaErrorHandler.js');
                if (await handleQuotaError(parsedBody.data, 'messages')) {
                    const quotaHandledError = new Error('QUOTA_HANDLED');
                    Object.assign(quotaHandledError, { isQuotaHandled: true, workerError });
                    throw quotaHandledError;
                }
            } catch (quotaError) {
                if (quotaError?.message === 'QUOTA_HANDLED' || quotaError?.isQuotaHandled) {
                    throw quotaError;
                }
                console.warn('Failed to process quota error payload:', quotaError);
            }
        }

        console.error('hangout-worker-request: Worker error:', response.status, workerError, 'URL:', workerUrl);
        const workerRequestError = new Error(`Worker error: Status ${response.status} - ${workerError.message}`);
        Object.assign(workerRequestError, {
            status: response.status,
            code: workerError.code,
            workerError
        });
        throw workerRequestError;
    }
    return response;
}
